import { z } from 'zod';
import { optionTypeSchema } from '../../../helper';
import { salesPointSchema, ISalesPoint } from './salesPoint';
import { IFarm } from '../farm/farm';

/**
 * Schema for validating sales point form data.
 * Extends the base sales point schema with form-specific validations.
 * Replaces ID fields with option type selections.
 *
 * @property {string} name - Sales point name (max 255 characters)
 * @property {Array<OptionType>} farms - Array of farm selections with label/value pairs
 */
export const salesPointFormSchema = salesPointSchema.extend({
    farms: z.array(optionTypeSchema).nonempty('Required'),
}).omit({
    // Omit the original ID field
    farmIds: true,
});

/**
 * Default form schema for new sales points.
 * Makes all required fields optional with empty defaults.
 */
export const salesPointDefaultFormSchema = salesPointFormSchema.extend({
    name: z.string().max(255, 'Name can only be up to 255 characters long.').optional().default(''),
    farms: z.array(optionTypeSchema).default([]),
});

/**
 * TypeScript interface representing Sales Point form values.
 * Generated from the Zod schema for type safety.
 */
export type ISalesPointFormValues = z.infer<typeof salesPointFormSchema>;

/**
 * TypeScript interface representing Sales Point default form values.
 * Generated from the Zod schema for type safety.
 */
export type ISalesPointDefaultFormValues = z.infer<typeof salesPointDefaultFormSchema>;

/**
 * Factory function to create form values for a sales point.
 * Handles both new and existing sales points.
 *
 * @param {ISalesPoint} [salesPoint] - Optional existing sales point to populate form
 * @param {Array<IFarm>} [farms] - Array of farms to use for mapping
 * @returns {ISalesPointFormValues} Form values with appropriate defaults
 */
export const SalesPointFormValues = (salesPoint ?: ISalesPoint, farms ?: Array<IFarm>) : ISalesPointFormValues | ISalesPointDefaultFormValues => {
    // Handle the default case (new sales point)
    if (!salesPoint) {
        return salesPointDefaultFormSchema.parse({});
    }

    const { farmIds, ...rest } = salesPoint;

    // Transform the ISalesPoint data into ISalesPointFormValues structure
    return salesPointFormSchema.parse({
        ...rest,
        farms: farms?.filter(x =>
            farmIds?.some(y => y === x.id)
        ).map(x => ({
            label: x.name,
            value: x.id,
        })) ?? [],
    });
};

/**
 * Validates sales point form values.
 * Logs any validation errors to the console.
 *
 * @param {ISalesPointFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation passes, false otherwise
 */
export const validateSalesPointForm = (formValues : ISalesPointFormValues) : boolean => {
    try {
        salesPointFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Sales point form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
